import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import { useCustomizablePopover } from 'common';
import { bool, func, shape } from 'prop-types';
import { Box, FormControlLabel, FormGroup, Paper, Switch, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRolePermissions } from 'hooks';
import NoAvailableCard from 'common/NoAvailableCard';
import PersistentServerSideDataGrid from 'common/PersistentServerSideDataGrid';
import usePersistentState from 'hooks/usePersistentState';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';
import { formatNumber } from 'utils/helpers/format';
import {
  buildSelfAuthListViewQueryParams,
  DEFAULT_SELF_AUTH_LIST_VIEW_PAGE_SIZE,
  DEFAULT_SELF_AUTH_LIST_VIEW_PAGINATION_MODEL,
  DEFAULT_SELF_AUTH_LIST_VIEW_SORT_MODEL,
  getSelfAuthListViewErrorMessage,
  SELF_AUTH_LIST_VIEW_PAGE_SIZE_OPTIONS,
} from './selfAuthListViewUtils';
import selfAuthorizationsEndpoints from './selfAuthorizationsEndpoints';

const {
  useGetSelfAuthorizationsListViewQuery,
  useLazyExportSelfAuthorizationsQuery,
  useToggleSelfAuthorizationActivationMutation,
  useUpdateSelfAuthorizationExtendedLoadingMutation,
} = selfAuthorizationsEndpoints;

const PAGE_SIZE_STORAGE_KEY = 'selfAuthorizationsListView.pageSize';
const MAX_VISIBLE_SUPPLIERS = 10;

const arePaginationModelsEqual = (left = {}, right = {}) =>
  left.page === right.page && left.pageSize === right.pageSize;

const areSortModelsEqual = (left = [], right = []) =>
  JSON.stringify(left) === JSON.stringify(right);

const NoRowsOverlay = () => (
  <NoAvailableCard suggestions="Try adjusting your search or filter options." type="data" />
);

const SelfAuthorizationsListView = ({
  appliedFilters,
  isProductLoadingManage,
  onExportingChange,
}) => {
  const dispatch = useDispatch();
  const [triggerExport] = useLazyExportSelfAuthorizationsQuery();
  const [toggleActivation] = useToggleSelfAuthorizationActivationMutation();
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    onExportingChange?.(isExporting);
  }, [isExporting, onExportingChange]);
  const { isSubscriber } = useUserRolePermissions();
  const navigate = useNavigate();
  const { currentAffiliateId, dataCompanyId } = useParams();
  const [updateSelfAuthorizationExtendedLoading, { isLoading: isUpdatingExtendedLoading }] =
    useUpdateSelfAuthorizationExtendedLoadingMutation();
  const isUpdatingExtendedLoadingRef = useRef(isUpdatingExtendedLoading);
  isUpdatingExtendedLoadingRef.current = isUpdatingExtendedLoading;
  const [persistedPageSize, setPersistedPageSize] = usePersistentState(
    PAGE_SIZE_STORAGE_KEY,
    DEFAULT_SELF_AUTH_LIST_VIEW_PAGE_SIZE
  );
  const [paginationModel, setPaginationModel] = useState({
    ...DEFAULT_SELF_AUTH_LIST_VIEW_PAGINATION_MODEL,
    pageSize: Number.isFinite(Number(persistedPageSize))
      ? Number(persistedPageSize)
      : DEFAULT_SELF_AUTH_LIST_VIEW_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState(DEFAULT_SELF_AUTH_LIST_VIEW_SORT_MODEL);
  const [activeRowId, setActiveRowId] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { handlePopoverOpen, handlePopoverClose, openPopover, Popover } = useCustomizablePopover();
  const affiliate = appliedFilters?.affiliate;

  useEffect(() => {
    setPersistedPageSize(paginationModel.pageSize);
  }, [paginationModel.pageSize, setPersistedPageSize]);

  useEffect(() => {
    if (!openPopover) {
      setIsExpanded(false);
    }
  }, [openPopover]);

  useEffect(() => {
    setIsExpanded(false);
  }, [activeRowId]);

  const queryParams = useMemo(
    () =>
      buildSelfAuthListViewQueryParams({
        appliedFilters,
        paginationModel,
        sortModel,
      }),
    [appliedFilters, paginationModel, sortModel]
  );

  const {
    currentData: selfAuthData = {},
    isFetching: pending,
    error: selfAuthError,
  } = useGetSelfAuthorizationsListViewQuery(
    { params: queryParams },
    {
      skip: !affiliate?.customerIdName || affiliate?.dataCompanyId === undefined,
    }
  );

  const rows = useMemo(() => selfAuthData?.rows || [], [selfAuthData?.rows]);
  const total = selfAuthData?.total || 0;
  const error = selfAuthError ? getSelfAuthListViewErrorMessage(selfAuthError) : '';

  useEffect(() => {
    if (!selfAuthError) {
      return;
    }

    const isAbortError =
      selfAuthError?.status === 'FETCH_ERROR' &&
      String(selfAuthError?.error || '').includes('AbortError');

    if (isAbortError) {
      return;
    }

    dispatch(
      createSnack({
        message: error,
        options: { persist: true, variant: 'error' },
        title: 'Action Not Completed',
      })
    );
  }, [dispatch, error, selfAuthError]);

  useEffect(() => {
    if (paginationModel?.page === 0) {
      return;
    }

    setPaginationModel(previousModel => ({ ...previousModel, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  const confirmToggleActivation = useCallback(
    async row => {
      const isActive = row.activeStatus === true;
      const authType = row.authorizationType.toLowerCase();
      const needsProductSuffix = ['base', 'custom'].includes(authType);
      const activationType = authType.replaceAll(' ', '-') + (needsProductSuffix ? '-product' : '');

      await toggleActivation({
        activationType,
        body: {
          affiliate: {
            companyCustomerIdName: affiliate.customerIdName,
            dataCompanyId: affiliate.dataCompanyId,
          },
          locationId: row.locationId,
          productId: row.productId,
        },
        isActive,
        params: {
          productLocationAuthorizationId: row.productLocationAuthorizationId,
        },
      });
    },
    [toggleActivation, affiliate]
  );

  const handleActivationSwitchChange = useCallback(
    row => {
      const isActive = row.activeStatus === true;
      const action = isActive ? 'deactivate' : 'activate';
      const isBase = row.authorizationType?.toLowerCase() === 'base';

      const warningList = isActive
        ? [
            'All Supplier Authorizations associated with this self authorization will also be deactivated.',

            ...(isBase
              ? [
                  'All custom product self authorizations utilizing this base component in the recipe will also be deactivated',
                ]
              : []),
            'Once done, this action cannot be undone. All authorizations will have to be set up again if reactivated.',
          ]
        : undefined;

      dispatch(
        openConfirmationDialog({
          confirmAction: () => confirmToggleActivation(row),
          header: `${isActive ? 'Deactivate' : 'Activate'} Authorization`,
          message: `Are you sure you want to ${action} the authorization for ${row.productIdn} ${row.productLongName} at ${row.locationIdn} ${row.locationName}?`,
          warningList,
        })
      );
    },
    [confirmToggleActivation, dispatch]
  );

  const submitExtendedLoadingUpdate = useCallback(
    async row => {
      try {
        await updateSelfAuthorizationExtendedLoading({
          extendedLoadingActivated: row?.extendedLoadingActivated,
          productLocationAuthorizationId: row?.productLocationAuthorizationId,
        }).unwrap();

        dispatch(
          createSnack({
            message: ` Extended Loading has been successfully turned ${
              row?.extendedLoadingActivated ? 'off' : 'on'
            } .`,
            options: { autoHideDuration: 4000, variant: 'success' },
            title: 'Action Completed',
          })
        );
      } catch (e) {
        const errorMessage =
          e?.data?.detail ||
          e?.data?.title ||
          e?.error ||
          `Due to an error, extended loading could not be turned ${
            row?.extendedLoadingActivated ? 'off' : 'on'
          }.`;
        dispatch(
          createSnack({
            message: errorMessage,
            options: {
              persist: true,
              variant: 'error',
            },
            title: 'Action Not Completed',
          })
        );
      }
    },
    [dispatch, updateSelfAuthorizationExtendedLoading]
  );

  const handleExtendedLoadingSwitchChange = useCallback(
    row => {
      if (!row?.productLocationAuthorizationId) {
        return;
      }

      const productDisplay = `${row?.productIdn || ''} ${row?.productLongName || ''}`.trim();
      const locationDisplay = row?.locationDisplay || row?.locationIdn || '';
      const isDeactivating = Boolean(row?.extendedLoadingActivated);

      dispatch(
        openConfirmationDialog({
          confirmAction: () => submitExtendedLoadingUpdate(row),
          header: isDeactivating ? 'Deactivate Extended Loading?' : 'Activate Extended Loading?',
          message: isDeactivating
            ? `Extended Loading for ${productDisplay} will be deactivated at ${locationDisplay} and will prevent extended loading for all Supplier Authorizations associated with this Self Authorization.`
            : `Extended Loading for ${productDisplay} will be activated at ${locationDisplay} and will allow extended loading for all Supplier Authorizations associated with this Self Authorization.`,
        })
      );
    },
    [dispatch, submitExtendedLoadingUpdate]
  );

  const columns = useMemo(
    () => [
      {
        align: 'center',
        field: 'activeStatus',
        flex: 0.9,
        headerAlign: 'center',
        headerName: 'Status',
        minWidth: 160,
        renderCell: ({ row }) => {
          if (typeof row?.activeStatus !== 'boolean') return null;

          return (
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={row.activeStatus}
                    disabled={!isProductLoadingManage}
                    onChange={() => handleActivationSwitchChange(row)}
                    size="medium"
                  />
                }
                label={row.activeStatus ? 'On' : 'Off'}
                labelPlacement="start"
                sx={{ justifyContent: 'center', m: 0, width: '100%' }}
              />
            </FormGroup>
          );
        },
        sortable: true,
      },
      {
        align: 'left',
        field: 'locationDisplay',
        flex: 1.4,
        headerAlign: 'left',
        headerName: 'Location',
        minWidth: 200,
        sortable: true,
      },
      {
        align: 'left',
        field: 'pidxCode',
        flex: 0.8,
        headerAlign: 'left',
        headerName: 'PIDX Code',
        minWidth: 120,
        sortable: true,
      },
      {
        align: 'left',
        field: 'productIdn',
        flex: 0.9,
        headerAlign: 'left',
        headerName: 'Product ID',
        minWidth: 140,
        sortable: true,
      },

      {
        align: 'left',
        field: 'productLongName',
        flex: 1.3,
        headerAlign: 'left',
        headerName: 'Product Name',
        minWidth: 220,
        sortable: true,
      },
      {
        align: 'left',
        field: 'authorizationType',
        flex: 0.9,
        headerAlign: 'left',
        headerName: 'Auth Type',
        minWidth: 140,
        sortable: true,
      },
      {
        align: 'left',
        field: 'supplierCount',
        flex: 0.7,
        headerAlign: 'left',
        headerName: 'Suppliers',
        minWidth: 110,
        renderCell: params => {
          if (!params.value || params.value <= 0) {
            return <Typography variant="body2">0</Typography>;
          }

          return (
            <Box
              onMouseEnter={event => {
                setActiveRowId(params.row.id);
                handlePopoverOpen(event);
              }}
              onMouseLeave={event => handlePopoverClose(event)}
              sx={{ cursor: 'pointer', display: 'inline-flex', py: 1 }}
            >
              <Typography sx={{ pointerEvents: 'none', textUnderlineOffset: '2px' }} variant="link">
                {params.value}
              </Typography>
            </Box>
          );
        },
        sortable: false,
      },
      {
        align: 'center',
        field: 'extendedLoading',
        flex: 1,
        headerAlign: 'center',
        headerName: 'Extended Loading',
        minWidth: 190,
        renderCell: params => {
          const { row } = params;
          const isExtendedLoadingAllowed = Boolean(row?.extendedLoading);
          const isExtendedLoadingActivated = Boolean(row?.extendedLoadingActivated);
          const isAuthorizationActive = Boolean(row?.activeStatus);

          if (isSubscriber) {
            if (!isExtendedLoadingAllowed) {
              return <Typography variant="body2">Not Allowed</Typography>;
            }

            return (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isExtendedLoadingActivated}
                      disabled={
                        !isAuthorizationActive ||
                        !isProductLoadingManage ||
                        isUpdatingExtendedLoadingRef.current
                      }
                      onChange={() => handleExtendedLoadingSwitchChange(row)}
                      slotProps={{ input: { 'aria-label': 'Extended Loading toggle' } }}
                    />
                  }
                  label={isExtendedLoadingActivated ? 'On' : 'Off'}
                  labelPlacement="start"
                  size="medium"
                />
              </FormGroup>
            );
          }
          const firstVisibleRowId = params.api?.getSortedRowIds?.()[0];
          const isFirstrow = firstVisibleRowId === row?.id;
          if (isFirstrow) {
            return null;
          }
          return (
            <Paper
              elevation={1}
              onClick={() =>
                navigate(`/affiliate/${currentAffiliateId}/${dataCompanyId}/swift-plus`, {
                  state: { fromSelfAuthGridNonSwift: false },
                })
              }
              sx={{
                border: theme => `1px solid ${theme.palette.orange.dark}`,
                boxShadow: 'none',
                color: 'text.secondary',
                cursor: 'pointer',
                fontFamily: 'overPass',
                fontSize: '12px',
                fontWeight: 400,
                height: '80px',
                overflowWrap: 'visible',
                position: 'fixed',
                px: 1,
                py: 2,
                textAlign: 'center',
                top: '50%',
                transform: 'translateY(-50%)',
                whiteSpace: 'normal',
                width: '120px',
              }}
            >
              Enroll in{' '}
              <Box component="span" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                Swift+
              </Box>{' '}
              <br />
              to enable
              <br />
              Extended Loading
            </Paper>
          );
        },
        sortable: true,
      },
    ],
    [
      handlePopoverClose,
      handlePopoverOpen,
      isProductLoadingManage,
      isSubscriber,
      handleActivationSwitchChange,
      handleExtendedLoadingSwitchChange,
      currentAffiliateId,
      dataCompanyId,
      navigate,
    ]
  );

  const handlePaginationModelChange = nextPaginationModel => {
    if (arePaginationModelsEqual(nextPaginationModel, paginationModel)) {
      return;
    }

    setPaginationModel(nextPaginationModel);
  };

  const handleSortModelChange = nextSortModel => {
    const normalizedSortModel =
      nextSortModel?.length > 0 ? [nextSortModel[nextSortModel.length - 1]] : [];

    if (areSortModelsEqual(normalizedSortModel, sortModel)) {
      return;
    }

    setSortModel(normalizedSortModel);
    setPaginationModel(previousModel => ({ ...previousModel, page: 0 }));
  };

  const handleExportAllRows = useCallback(async () => {
    setIsExporting(true);
    try {
      const { page, pageSize, ...exportQueryParams } = queryParams;
      const result = await triggerExport(exportQueryParams, false);

      if (result?.error) throw result.error;

      const csvBlob =
        result?.data instanceof Blob
          ? result.data
          : new Blob([result?.data || ''], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(csvBlob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'self-authorizations.csv';
      anchor.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  }, [queryParams, triggerExport]);

  const handleResetView = () => {
    setPaginationModel(previousModel => ({
      ...DEFAULT_SELF_AUTH_LIST_VIEW_PAGINATION_MODEL,
      pageSize: previousModel.pageSize,
    }));
    setSortModel(DEFAULT_SELF_AUTH_LIST_VIEW_SORT_MODEL);
  };

  const activeRow = rows.find(row => row.id === activeRowId);
  const popoverSuppliers = activeRow?.suppliers || [];
  const isUnavailable = activeRow?.supplierCount > 0 && popoverSuppliers.length === 0;
  const visibleSuppliers = isExpanded
    ? popoverSuppliers
    : popoverSuppliers.slice(0, MAX_VISIBLE_SUPPLIERS);
  const remainingCount = popoverSuppliers.length - MAX_VISIBLE_SUPPLIERS;

  return (
    <>
      <PersistentServerSideDataGrid
        columns={columns}
        defaultPageSize={paginationModel.pageSize}
        disableColumnMenu
        disableColumnReorder={isExporting}
        disabledExport={total === 0}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        id="self-authorizations-list-view"
        isExporting={isExporting}
        loading={pending || isExporting}
        onExportAllRows={handleExportAllRows}
        onPaginationChange={handlePaginationModelChange}
        onResetView={handleResetView}
        onSortChange={handleSortModelChange}
        pageSizeOptions={SELF_AUTH_LIST_VIEW_PAGE_SIZE_OPTIONS}
        paginationStatus={`${formatNumber(total)} Authorizations`}
        rows={rows}
        slots={{
          noRowsOverlay: () => <NoRowsOverlay />,
        }}
        totalRows={total}
      />
      <Popover
        id="self-auth-suppliers-popover"
        popoverFooterContent={
          !isExpanded && remainingCount > 0 ? (
            <Typography
              onClick={() => setIsExpanded(true)}
              sx={{ cursor: 'pointer', textUnderlineOffset: '2px' }}
              variant="link"
            >
              + {remainingCount} More
            </Typography>
          ) : null
        }
        popoverHeaderContent={
          <Typography sx={{ lineHeight: 1.5 }} variant="body1bold">
            Supplier Authorizations
          </Typography>
        }
        popoverMainContent={
          isUnavailable ? (
            <Typography color="error" variant="body2">
              Unable to retrieve suppliers
            </Typography>
          ) : (
            <Stack spacing={1}>
              {visibleSuppliers.map(supplier => (
                <Box
                  key={supplier.id}
                  sx={{
                    alignItems: 'baseline',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.5,
                      maxWidth: 220,
                      mr: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    variant="body1"
                  >
                    {supplier.supplierName}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.57,
                    }}
                    variant="subtitle2"
                  >
                    {supplier.authorizationType}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )
        }
      />
    </>
  );
};

SelfAuthorizationsListView.propTypes = {
  appliedFilters: shape({
    affiliate: shape({}),
  }),
  isProductLoadingManage: bool,
  onExportingChange: func,
};

SelfAuthorizationsListView.defaultProps = {
  appliedFilters: {
    affiliate: null,
  },
  isProductLoadingManage: false,
  onExportingChange: null,
};

export default SelfAuthorizationsListView;
